import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Rocket, Cpu, Database, Satellite } from "lucide-react";
import HeroSection from "./HeroSection";
import Aos from "aos";

const Project2 = () => {
  useEffect(() => {
    Aos.init({
          duration: 1000,
          once: true,
        });
    window.scrollTo(0, 0);
  }, []);

  const specifications = [
    { label: "Maximum Altitude", note: "18000 ft AMSL" },
    { label: "Maximum Endurance", note: "16 HOURS" },
    { label: "Maximum Range", note: "2000 km" },
    { label: "Maximum Climb Rate", note: "3 ms^-1" },
    { label: "Maximum Descent", note: "6 ms^-1" },
    { label: "Maximum Tilt Angle", note: "30 degree" },
    { label: "Cornering Speed Limits", note: "50 kts" },
    { label: "Lowest Achievable Speed", note: "50 kts" },
    { label: "Maximum Cruising Speed", note: "65 kts" }
  ];

  const features = [
    {
      icon: <Satellite className="w-6 h-6" />,
      title: "Payload Efficiency Challenges",
      description: "A major challenge for defense airborne platforms is maximizing payload efficiency, which is directly influenced by size, weight, and power (SWaP). These parameters are central to our new product, and are considered one of the most critical specifications during product and platform development. Balancing these aspects with system performance and multifunctional capabilities remains a significant design challenge."
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "SWaP Optimization",
      description: "SWaP has become the key driving factor in defense technology, pushing engineers to make difficult trade-offs between enhancing performance and accommodating shrinking physical footprints. The importance of SWaP extends beyond simple optimization; it requires making decisions that could enable a technological leap forward, redefining the future of defense technologies."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Real-Time Data Demands",
      description: "The pursuit of optimized SWaP will directly impact the future of airborne ISR platforms. As design footprints continue to shrink, the pressure to pack more performance into these systems increases. By focusing on SWaP, we can meet the growing demand for real-time sensor data while ensuring the efficient use of power, weight, and size."
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "Technological Advancements",
      description: "Ultimately, leveraging the SWaP equation will enable defense technologies to evolve in a way that maximizes operational efficiency and performance. As systems become smaller, more powerful, and more capable, they will redefine what is possible in ISR, paving the way for innovations that support faster, more precise, and effective military operations."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Navbar />

      {/* Hero Section */}
      {/* <div className="bg-customBlue text-white min-h-[20vh] flex items-center ">
        <div className="max-w-6xl mx-auto px-6 w-full">
          <div className="flex flex-col items-center text-center space-y-4">
            
            <h1 className="font-nova-square text-4xl font-thin text-black">
            RiGEL UCAV On SWaP Platform
            ​</h1>
            <div className="text-sm breadcrumbs text-black">
              <Link to="/" >Home</Link>
              <span className="mx-2">></span>
              <Link to="/project" >Projects</Link>
              <span className="mx-2">></span>
              <span><Link to={"/project/project2"}>RiGEL UCAV On SWaP Platform​</Link></span>
            </div>
          </div>
        </div>
      </div> */}

      <HeroSection 
        title="RiGEL UCAV On SWaP Platform" 
        breadcrumb={
          <>
            <Link to={"/project"}>Projects</Link> {' > '} 
            <Link to={"/project/project2"}>RiGEL UCAV On SWaP Platform</Link>
          </>
        } 
      />

      <main className="max-w-6xl mx-auto px-6 py-12" data-aos="fade-up">
        {/* Overview Section */}
        <section className="mb-16">
          <h2 className="text-2xl w-52 font-bold mb-6 text-gray-900 relative after:absolute after:bottom-0 after:left-0 after:w-[96%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Project Overview</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 text-justify">
              The nation is entering a new era of defense, underpinned by cutting-edge intelligence, surveillance, 
              and reconnaissance (ISR) capabilities. Achieving mission success now relies heavily on maintaining dominance in the electromagnetic spectrum, 
              a critical component for military operations. The evolution of ISR technologies demands advanced systems capable of processing immense amounts of data collected from modern sensors, 
              which often overwhelm traditional processing techniques.
              </p>
            </div>
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 text-justify">
              Our vision is to enhance ISR sensing and control systems by incorporating machine learning (ML) 
              and deep learning (DL) technologies. These technologies will allow us to synthesize data faster and more accurately, enabling military 
              forces to make better-informed command and control decisions. With these advancements, ISR platforms can operate at a level of efficiency and speed that was
               previously unattainable.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold w-40 mb-6 text-gray-900  relative after:absolute after:bottom-0 after:left-0 after:w-[100%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-lightBlue p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-blue-600">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-lg  text-gray-700 text-justify">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Technical Specifications */}
        <section>
        <h2 className="text-2xl font-bold mb-6 text-gray-900 relative after:absolute after:bottom-0 after:left-0 after:w-[75%] after:sm:w-[25%] after:2xl:w-[25%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Technical Specifications</h2>
          <div className="bg-lightBlue rounded-lg shadow-sm border border-gray-100">
            <div className="grid md:grid-cols-2 gap-4 p-6 hover:shadow-lg transition-shadow">
              {specifications.map((spec, index) => (
                <div key={index} className="flex flex-col hover:shadow-lg transition-shadow px-4 py-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg  text-gray-600">{spec.label}</span>
                    {/* <span className="text-lg text-gray-900 font-medium">{spec.value}</span> */}
                  </div>
                  {spec.note && (
                    <span className="text-lg text-sm text-gray-500">{spec.note}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Project2; 